/**
 * The story band's six branches — the demo's content, as data.
 *
 * ## What the band argues
 *
 * One widget, six unrelated questions, six DIFFERENT interfaces. The point is
 * not that a chat can answer; it is that nobody wrote any of these six screens.
 * So the six are chosen to be maximally unlike each other — a commerce picker,
 * a trip itinerary, a governance draft, a chart, a docs answer, a shipment
 * tracker — because six variations on "a list of results" would prove nothing.
 *
 * ## Why the content is typed rather than free-form
 *
 * Each branch declares a `body` from a CLOSED union of card shapes. A branch
 * cannot invent a seventh layout without adding a member here and a renderer in
 * `BranchCard`, which is the point: the demo shows composition from a fixed
 * vocabulary, exactly like the product's own composition surface, and a demo
 * that could render anything would be misrepresenting it.
 *
 * ## Localization
 *
 * Every visible string is a catalog KEY, resolved at render. The kanji tiles in
 * the trip branch are the one exception — they are place names in their own
 * script, which is content rather than UI copy, and translating 東京 into
 * "Tokyo" in the German catalog would defeat the tile.
 */

import { BRAND } from '@/components/bedrock';

/** Where a branch sits on the 1618×860 stage, and which port it leaves from. */
export interface BranchPlacement {
  /** Inline-start offset in stage units. Mirrors under RTL. */
  readonly x: number;
  /** Distance from the stage's bottom edge, in stage units. */
  readonly bottom: number;
  /** The connector path, in stage coordinates. */
  readonly path: string;
  /** Which gradient the connector paints with. */
  readonly grad: 'a' | 'b' | 'c';
}

export type BranchBody =
  | { readonly kind: 'products' }
  | { readonly kind: 'itinerary' }
  | { readonly kind: 'policy' }
  | { readonly kind: 'chart' }
  | { readonly kind: 'answer' }
  | { readonly kind: 'shipment' };

export interface Branch {
  readonly id: string;
  /** Catalog key for the question typed into this branch's composer. */
  readonly askKey: string;
  /** Catalog key for the badge on the result card. */
  readonly badgeKey: string;
  /** Catalog key for the mono meta on the result card. */
  readonly metaKey: string;
  /** The card's accent — drives the badge dot and the top rule. */
  readonly accent: string;
  /** The top rule's gradient end, so each card's rule is distinct. */
  readonly accentTo: string;
  readonly body: BranchBody;
  readonly placement: BranchPlacement;
  /** Seconds into the build when this branch's composer appears. */
  readonly at: number;
}

/**
 * Stage geometry. The stage is authored at a fixed 1618×860 and then SCALED to
 * whatever width it is given, so every coordinate below is authored once and
 * never re-tuned per breakpoint — the alternative (a responsive layout for a
 * diagram with six curved connectors) would need the paths recomputed at every
 * width.
 */
export const STAGE = { w: 1618, h: 860 } as const;

/** The central widget's box, in stage units. */
export const WIDGET = { x: 656, y: 170, w: 306, h: 520 } as const;

export const BRANCHES: readonly Branch[] = [
  {
    id: 'products',
    askKey: 'home.story.products.ask',
    badgeKey: 'home.story.products.badge',
    metaKey: 'home.story.products.meta',
    accent: '#2563eb',
    accentTo: BRAND.blue,
    body: { kind: 'products' },
    placement: { x: 340, bottom: 528, path: 'M656 250 C 642 250 644 311 630 311', grad: 'a' },
    at: 5.9,
  },
  {
    id: 'itinerary',
    askKey: 'home.story.itinerary.ask',
    badgeKey: 'home.story.itinerary.badge',
    metaKey: 'home.story.itinerary.meta',
    accent: '#0891b2',
    accentTo: BRAND.cyan,
    body: { kind: 'itinerary' },
    placement: { x: 988, bottom: 528, path: 'M962 250 C 976 250 974 311 988 311', grad: 'c' },
    at: 6.1,
  },
  {
    id: 'policy',
    askKey: 'home.story.policy.ask',
    badgeKey: 'home.story.policy.badge',
    metaKey: 'home.story.policy.meta',
    accent: BRAND.purple,
    accentTo: BRAND.violet,
    body: { kind: 'policy' },
    placement: { x: 340, bottom: 36, path: 'M656 610 C 640 610 646 803 630 803', grad: 'b' },
    at: 6.4,
  },
  {
    id: 'chart',
    askKey: 'home.story.chart.ask',
    badgeKey: 'home.story.chart.badge',
    metaKey: 'home.story.chart.meta',
    accent: '#2563eb',
    accentTo: BRAND.cyan,
    body: { kind: 'chart' },
    placement: { x: 988, bottom: 36, path: 'M962 610 C 978 610 972 803 988 803', grad: 'a' },
    at: 6.6,
  },
  {
    id: 'answer',
    askKey: 'home.story.answer.ask',
    badgeKey: 'home.story.answer.badge',
    metaKey: 'home.story.answer.meta',
    accent: '#0f766e',
    accentTo: '#34d399',
    body: { kind: 'answer' },
    placement: { x: 14, bottom: 329, path: 'M656 430 C 560 430 420 455 304 455', grad: 'b' },
    at: 7.2,
  },
  {
    id: 'shipment',
    askKey: 'home.story.shipment.ask',
    badgeKey: 'home.story.shipment.badge',
    metaKey: 'home.story.shipment.meta',
    accent: '#8a5a06',
    accentTo: '#f59e0b',
    body: { kind: 'shipment' },
    placement: { x: 1314, bottom: 329, path: 'M962 430 C 1058 430 1198 455 1314 455', grad: 'c' },
    at: 7.4,
  },
];

/**
 * The central widget's scripted transcript. Four assistant turns and one
 * visitor turn, in the order they land. `tool` marks a turn that shows a tool
 * chip above its prose — the product's own "this ran, and here is the receipt"
 * affordance, which is the detail that makes the mock read as the real widget
 * rather than as a generic chat bubble.
 */
export interface WidgetTurn {
  readonly id: string;
  readonly side: 'agent' | 'user';
  readonly textKey: string;
  readonly toolKey?: string;
  readonly toolMetaKey?: string;
  /** Seconds into the build when this turn lands. */
  readonly at: number;
}

export const WIDGET_TURNS: readonly WidgetTurn[] = [
  { id: 'greet', side: 'agent', textKey: 'home.story.widget.greet', at: 2.35 },
  { id: 'ask', side: 'user', textKey: 'home.story.widget.ask', at: 4.06 },
  {
    id: 'install',
    side: 'agent',
    textKey: 'home.story.widget.install',
    toolKey: 'home.story.widget.installTool',
    toolMetaKey: 'home.story.widget.installMeta',
    at: 4.6,
  },
  { id: 'spec', side: 'agent', textKey: 'home.story.widget.spec', at: 7.9 },
  {
    id: 'scan',
    side: 'agent',
    textKey: 'home.story.widget.scan',
    toolKey: 'home.story.widget.scanTool',
    toolMetaKey: 'home.story.widget.scanMeta',
    at: 10.4,
  },
  { id: 'learn', side: 'agent', textKey: 'home.story.widget.learn', at: 12.1 },
];

/** When the one-shot build finishes and the stage settles into its idle life. */
export const BUILD_END_S = 12.6;
